import { useRef, useState, useEffect } from 'react';

import { ILottie } from '@lottielab/lottie-player';
import { useAuthInfo, useLogoutFunction } from '@propelauth/react';
import { Home, BookOpen, Users, Settings, LogOut, Building2, Mail, Shield, Notebook, CircleUser } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';

import { Permission } from '../../../../../backend/src/auth/permissions/permission';
import { EnvVars } from '../../../../environment';
import WexlerLogoAnimation from '@/assets/animations/WexlerLogo.json';
import LottieAnimation from '@/components/atoms/Lottie';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/atoms/Popover';
import { CollapsibleSidebarItem } from '@/components/organisms/Sidebar/CollapsibleSidebarItem';
import { PopoverButton } from '@/components/organisms/Sidebar/PopoverButton';
import {
  Sidebar,
  SidebarContent,
  SidebarGroup,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarFooter,
  SidebarHeader,
} from '@/components/organisms/Sidebar/Sidebar';
import { useSidebar } from '@/components/organisms/Sidebar/Sidebar';
import { useUserContext } from '@/Contexts/User';
import { useHasPermission } from '@/hooks/useHasPermission';

export function AppSidebar() {
  const logoutFn = useLogoutFunction();
  const authInfo = useAuthInfo();
  const navigate = useNavigate();

  const { user } = useUserContext();
  const { hasPermission } = useHasPermission();
  const { state: sidebarState, toggleSidebar } = useSidebar();

  const [openGroup, setOpenGroup] = useState<string | null>(null);

  const handleClickLogout = () => {
    logoutFn(true);
    toast.info('See you again soon.');
  };

  const handleClickSettings = () => {
    navigate('/app/settings');
  };

  const openBullboard = async () => {
    const accessToken = await authInfo.tokens.getAccessToken();
    window.open(`${EnvVars.API_URL}/admin/queues/?token=${encodeURIComponent(accessToken ?? '')}`, '_blank');
  };

  const SIDEBAR_ITEM_CLASSNAME = 'hover:bg-gray-100/20 cursor-pointer';

  const animationRef = useRef<ILottie>(null);
  const switchState = (stateName: string) => {
    animationRef.current?.interactivity?.trigger(stateName);
  };
  useEffect(() => {
    if (sidebarState === 'expanded') {
      switchState('start');
      return;
    }
    switchState('end');
  }, [sidebarState]);

  const sidebarConfig = {
    home: {
      icon: <Home />,
      title: 'Home',
      onClick: () => navigate('/app/chronos/matters'),
    },
    resources: {
      icon: <BookOpen className="h-4 w-4" />,
      title: 'Resources',
      subItems: [
        {
          title: 'Guide',
          onClick: () => navigate('/app/chronos/guide'),
        },
        {
          title: 'Client Materials',
          onClick: () => navigate('/app/chronos/client-materials'),
        },
      ],
    },
    team: {
      icon: <Users className="h-4 w-4" />,
      title: 'Team',
      subItems: [
        {
          title: 'Usage',
          onClick: () => navigate('/app/chronos/usage-dashboard'),
        },
        {
          title: 'Users',
          onClick: () => navigate('/app/chronos/users'),
        },
        {
          title: 'Configuration',
          onClick: () => navigate('/app/chronos/configuration/credits'),
        },
      ],
    },
    admin: {
      icon: <Building2 className="h-4 w-4" />,
      title: 'Admin',
      subItems: [
        {
          title: 'Organisations',
          onClick: () => navigate('/app/chronos/organisations'),
        },
        {
          title: 'BullMQ',
          onClick: openBullboard,
        },
      ],
    },
    support: {
      icon: <Mail className="h-4 w-4" />,
      title: 'Support',
      subItems: [
        {
          title: 'support@wexler.ai',
          isLink: true,
          href: 'mailto:support@wexler.ai',
        },
        {
          title: '+44 20804 09060',
          isLink: true,
          href: 'tel:+442080409060',
        },
      ],
    },
    releaseNotes: {
      icon: <Notebook />,
      title: 'Release Notes',
      onClick: () => navigate('/app/release-notes'),
    },
    user: {
      icon: <CircleUser />,
      subItems: [
        {
          title: 'Settings',
          onClick: handleClickSettings,
        },
        {
          title: 'Privacy',
          onClick: () => navigate('/app/privacy'),
        },
        {
          title: 'Logout',
          onClick: handleClickLogout,
        },
      ],
    },
  };

  return (
    <Sidebar
      collapsible="icon"
      className="bg-gradient-to-b from-[#013e45] via-[#013e45] via-40% to-[#31c4ac] text-sm text-white"
    >
      <SidebarContent>
        <SidebarHeader className="text-3xl font-semibold">
          <SidebarMenu>
            <SidebarMenuItem>
              <SidebarMenuButton className="text-3xl font-semibold" onClick={() => toggleSidebar()}>
                <LottieAnimation
                  lottieRef={animationRef}
                  animationData={WexlerLogoAnimation}
                  className="absolute -top-12 left-1 h-32 w-32"
                />
              </SidebarMenuButton>
            </SidebarMenuItem>
          </SidebarMenu>
        </SidebarHeader>
        <SidebarGroup className="mt-4">
          <SidebarMenu>
            <SidebarMenuItem>
              <SidebarMenuButton tooltip="Home" asChild onClick={() => navigate('/app/chronos/matters')}>
                <div className={SIDEBAR_ITEM_CLASSNAME}>
                  <Home />
                  <span>Home</span>
                </div>
              </SidebarMenuButton>
            </SidebarMenuItem>
          </SidebarMenu>
        </SidebarGroup>

        <SidebarGroup>
          <SidebarMenu>
            <CollapsibleSidebarItem
              icon={sidebarConfig.resources.icon}
              title={sidebarConfig.resources.title}
              openGroup={openGroup}
              groupKey="resources"
              onOpenChange={(open) => setOpenGroup(open ? 'resources' : null)}
              subItems={sidebarConfig.resources.subItems}
            />
          </SidebarMenu>
        </SidebarGroup>

        {(hasPermission(Permission.UsageDashboardView) || hasPermission(Permission.UsersManage)) && (
          <SidebarGroup>
            <SidebarMenu>
              <CollapsibleSidebarItem
                icon={sidebarConfig.team.icon}
                title={sidebarConfig.team.title}
                openGroup={openGroup}
                groupKey="team"
                onOpenChange={(open) => setOpenGroup(open ? 'team' : null)}
                subItems={sidebarConfig.team.subItems?.filter((item) => {
                  if (item.title === 'Usage' && hasPermission(Permission.UsageDashboardView)) return true;
                  if (item.title === 'Users' && hasPermission(Permission.UsersManage)) return true;
                  if (
                    item.title === 'Configuration' &&
                    (hasPermission(Permission.IntegrationsManage) || hasPermission(Permission.TokenLimitsManage))
                  )
                    return true;
                  return false;
                })}
              />
            </SidebarMenu>
          </SidebarGroup>
        )}

        {user?.organisation.isGlobalAdmin && (
          <SidebarGroup>
            <SidebarMenu>
              <CollapsibleSidebarItem
                icon={sidebarConfig.admin.icon}
                title={sidebarConfig.admin.title}
                openGroup={openGroup}
                groupKey="admin"
                onOpenChange={(open) => setOpenGroup(open ? 'admin' : null)}
                subItems={sidebarConfig.admin.subItems}
              />
            </SidebarMenu>
          </SidebarGroup>
        )}

        <SidebarGroup>
          <SidebarMenu>
            <CollapsibleSidebarItem
              icon={sidebarConfig.support.icon}
              title={sidebarConfig.support.title}
              openGroup={openGroup}
              groupKey="support"
              onOpenChange={(open) => setOpenGroup(open ? 'support' : null)}
              subItems={sidebarConfig.support.subItems}
            />
          </SidebarMenu>
        </SidebarGroup>

        <SidebarFooter className="mt-auto">
          <SidebarMenu>
            <SidebarMenuItem className={SIDEBAR_ITEM_CLASSNAME}>
              <SidebarMenuButton tooltip="Release Notes" asChild onClick={() => navigate('/app/release-notes')}>
                <div>
                  <Notebook />
                  <span>Release Notes</span>
                </div>
              </SidebarMenuButton>
            </SidebarMenuItem>
            <SidebarMenuItem>
              <Popover>
                <PopoverTrigger asChild>
                  <SidebarMenuButton tooltip="User" className={SIDEBAR_ITEM_CLASSNAME}>
                    <CircleUser />

                    <div className="flex flex-col items-start">
                      <span className="w-32 truncate text-xs font-semibold">{user?.email}</span>
                      <span className="text-xs opacity-70">{user?.organisation?.name}</span>
                    </div>
                  </SidebarMenuButton>
                </PopoverTrigger>
                <PopoverContent
                  side="right"
                  align="start"
                  className="m-2 w-36 border-gray-700 bg-gray-800 p-0 text-white shadow-2xl"
                >
                  <div className="flex flex-col text-sm">
                    <div className="flex items-center gap-2 rounded-t-lg border-b border-b-gray-700 bg-gray-900 px-4 py-2 text-white">
                      <CircleUser size={16} />
                      User
                    </div>
                    {sidebarConfig.user.subItems?.map((item, index) => (
                      <PopoverButton
                        key={index}
                        icon={
                          item.title === 'Settings' ? (
                            <Settings size={16} />
                          ) : item.title === 'Privacy' ? (
                            <Shield size={16} />
                          ) : item.title === 'Logout' ? (
                            <LogOut size={16} />
                          ) : undefined
                        }
                        onClick={item.onClick}
                      >
                        {item.title}
                      </PopoverButton>
                    ))}
                  </div>
                </PopoverContent>
              </Popover>
            </SidebarMenuItem>
          </SidebarMenu>
        </SidebarFooter>
      </SidebarContent>
    </Sidebar>
  );
}
